import { useCallback, useEffect, useState } from 'react';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

import i18n from '@/i18n/i18n';

interface RecordWithMerchant {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
}

interface UseRecordMerchantSyncOptions {
  // 預設 false：record 一定要有 merchant_id（operator scope 內 belongs_to :merchant
  // 多數是 non-optional）。為 true 時表示 record 的 merchant 可為 null（如
  // PickingListPreset 的 organization-scope preset），此時 missing merchant_id 不會
  // 觸發 unavailable 流程。
  optional?: boolean;
  // 預設 true：record 不可用時 hook 自動 navigate(-1) + toast，wrapper 不需要消費
  // recordMerchantUnavailable flag。設成 false 時 wrapper 自行根據 flag 決定要
  // navigate / 渲染 Alert（極少數場景）。
  navigateOnUnavailable?: boolean;
}

interface UseRecordMerchantSyncResult {
  recordMerchantId: string | null;
  handleRecordLoaded: (record: RecordWithMerchant | null | undefined) => void;
  merchantDataLoading: boolean;
  // record 不可用：missing merchant_id (非 optional)，或 record 的 merchant 不在
  // operator accessible 內。預設 navigateOnUnavailable=true 時 hook 已 toast + navigate
  // 處理；wrapper 通常無需消費此 flag，僅在 navigateOnUnavailable=false 時才需要。
  recordMerchantUnavailable: boolean;
}

// Operator edit pages need this whenever a record `belongs_to :merchant` and the
// page renders merchant-scoped reference dropdowns (sales_channel, inventory_type,
// shipping_type, brand, supplier, etc.).
//
// The bug we are guarding against:
//   1. operator picks merchant B in the header
//   2. opens /operator/<resource>/<id>/edit for a record owned by merchant A
//   3. BE access-check passes (operator has both merchants)
//   4. without this hook, the form renders B's reference data while the record
//      stores A's foreign keys → antd Select falls back to raw UUIDs and a save
//      writes B's id into A's record
//
// Two unrecoverable states are surfaced as `recordMerchantUnavailable`:
//   (a) BE returned a record with no merchant_id but `optional` is false →
//       serializer broken / data corruption. Form must NOT render with header
//       current merchant (that re-triggers the bug class above).
//   (b) record's merchant is not in operator's accessible merchants → mid-session
//       permission revocation or stale OperatorDataContext cache.
//
// In both cases, the hook (by default) toasts a localized message and navigates
// back, so all 18 wrappers don't need to each remember to render an Alert. To
// opt out, pass `navigateOnUnavailable: false` and consume the flag manually.
//
// Canonical reference: pages/operator/Orders/Edit.jsx,
// pages/operator/ContactAddresses/Edit.tsx — this hook is the same pattern,
// extracted so every wrapper doesn't reinvent the state + effect + gate.
export function useRecordMerchantSync(
  options: UseRecordMerchantSyncOptions = {}
): UseRecordMerchantSyncResult {
  const { optional = false, navigateOnUnavailable = true } = options;
  const {
    merchants,
    currentMerchant,
    setCurrentMerchant,
    loading: globalLoading,
  } = useOperatorData();
  const navigate = useNavigate();

  const [recordMerchantId, setRecordMerchantId] = useState<string | null>(null);
  const [recordMerchantUnavailable, setRecordMerchantUnavailable] = useState(false);

  const handleRecordLoaded = useCallback(
    (record: RecordWithMerchant | null | undefined) => {
      if (!record) return;
      const merchantId = record.merchant_id ?? record.merchant?.id ?? null;
      if (merchantId) {
        setRecordMerchantId(merchantId);
        return;
      }
      if (optional) return; // 合法 null（如 org-scope preset）
      // BE 漏序列化 merchant_id ⇒ 資料毀損 / serializer broken。不能默默讓 form 用
      // header current merchant render（那會走回此 hook 要修的 bug 路徑），改成把
      // record 標為 unavailable，下方 effect 會 navigate + toast，merchantDataLoading
      // 維持 true 擋住 form。
      console.error(
        '[useRecordMerchantSync] record missing merchant_id (BE contract broken or non-optional belongs_to violated)',
        record
      );
      setRecordMerchantUnavailable(true);
    },
    [optional]
  );

  useEffect(() => {
    if (recordMerchantUnavailable) return;
    if (!recordMerchantId || merchants.length === 0) return;
    if (recordMerchantId === currentMerchant?.id) return;
    const matched = merchants.find(m => m.id === recordMerchantId);
    if (matched) {
      setCurrentMerchant(matched);
      return;
    }
    // record 的 merchant 不在 accessible 清單 ⇒ unrecoverable
    console.error(
      '[useRecordMerchantSync] record merchant not in accessible merchants — permission revocation or stale cache',
      { recordMerchantId, accessibleMerchantIds: merchants.map(m => m.id) }
    );
    setRecordMerchantUnavailable(true);
  }, [recordMerchantId, merchants, currentMerchant?.id, setCurrentMerchant, recordMerchantUnavailable]);

  // Unavailable 處理：toast localized message + 預設自動 navigate(-1)。
  // i18n key 區分兩種根因：recordMerchantId 為 null ⇒ missing；非 null ⇒ inaccessible。
  useEffect(() => {
    if (!recordMerchantUnavailable) return;
    const i18nKey =
      recordMerchantId === null
        ? 'common.error.record_missing_merchant'
        : 'common.error.record_merchant_inaccessible';
    message.error(i18n.t(i18nKey) as string);
    if (navigateOnUnavailable) {
      navigate(-1);
    }
  }, [recordMerchantUnavailable, recordMerchantId, navigateOnUnavailable, navigate]);

  // merchantDataLoading 在三種情況都為 true：
  //   1. global context 還沒準備好（merchants 還沒拉到）
  //   2. recordMerchantUnavailable — 擋住 form 在 navigate 完成前 flash 出錯 UI
  //   3. recordMerchantId 已知但與 currentMerchant 不同 — sync 過渡期
  const merchantDataLoading =
    globalLoading ||
    recordMerchantUnavailable ||
    (!!recordMerchantId && recordMerchantId !== currentMerchant?.id);

  return { recordMerchantId, handleRecordLoaded, merchantDataLoading, recordMerchantUnavailable };
}

export default useRecordMerchantSync;
